import React, { useRef, useState, useEffect } from 'react'
import ReactDOM from 'react-dom'

// Import stylesheets as raw text — injected into the Shadow DOM
// so they only apply to calculator content, fully isolated from the parent app.
// eslint-disable-next-line import/no-webpack-loader-syntax
import tailwindCSS from '!!raw-loader!./style.css'
// eslint-disable-next-line import/no-webpack-loader-syntax
import calculatorCSS from '!!raw-loader!./calculators.css'

/**
 * ShadowCalculator — renders children inside a Shadow DOM boundary.
 *
 * The parent PropStream app has global CSS (Helvetica Neue, normalize.css,
 * various resets) that would leak into calculator components. Shadow DOM
 * creates a hard isolation boundary — no parent CSS can cross it.
 */
export default function ShadowCalculator({ children, style }) {
  const hostRef = useRef(null)
  const [shadowRoot, setShadowRoot] = useState(null)

  useEffect(() => {
    if (!hostRef.current || hostRef.current.shadowRoot) {
      if (hostRef.current?.shadowRoot) setShadowRoot(hostRef.current.shadowRoot)
      return
    }

    const shadow = hostRef.current.attachShadow({ mode: 'open' })

    // Load Inter font into the main document head (fonts must be in light DOM)
    if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Inter"]')) {
      const fontLink = document.createElement('link')
      fontLink.rel = 'stylesheet'
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap'
      document.head.appendChild(fontLink)
    }

    // Strip @import from Tailwind CSS (font loaded via <link> above)
    const twCSS = tailwindCSS.replace(/@import\s*["'][^"']*fonts\.googleapis[^"']*["']\s*;?/g, '')

    // 1. Tailwind base styles (used by FixFlipAnalyzer legacy classes)
    const twStyle = document.createElement('style')
    twStyle.textContent = twCSS
    shadow.appendChild(twStyle)

    // 2. Calculator component styles (BEM classes, design tokens)
    const calcStyle = document.createElement('style')
    calcStyle.textContent = calculatorCSS
    shadow.appendChild(calcStyle)

    // React mount point inside the shadow root
    const mountPoint = document.createElement('div')
    mountPoint.id = 'shadow-calculator-root'
    mountPoint.style.cssText = [
      'height: 100%',
      'overflow-y: auto',
      'overflow-x: hidden',
      'font-family: Inter, ui-sans-serif, system-ui, sans-serif',
      'font-size: 14px',
      'color: #3c4654',
      '-webkit-font-smoothing: antialiased',
    ].join(';')
    shadow.appendChild(mountPoint)

    setShadowRoot(mountPoint)
  }, [])

  return (
    <div ref={hostRef} style={style || { height: '100%' }}>
      {shadowRoot && ReactDOM.createPortal(children, shadowRoot)}
    </div>
  )
}
